/**
 * PARAMETERS
 *  - $planPicker: (required) the element to be used as the plan picker
 *  - overrides: (optional) override certain values
 *
 * OVERRIDES
 *  - getVariantId: function
 *
 * EVENTS
 *  - subs:plan-picker:loaded - (window) when subs script is loaded
 *  - subs:plan-picker:init - ($planPicker) when subs instance is initialized
 *  - subs:plan-picker:updated - ($planPicker) when subs instance is updated
 */

(() => {
  if (window.subsPlanPicker) return Error('subs: subs already loaded');

  // subs Instances
  window.subsPlanPickerInstances = [];

  window.subsPlanPicker = ({ $planPicker, overrides = {} } = {}) => {
    // console.log($planPicker);
    try {
      const subs = {};

      // Log level
      subs.logLevel = 'info'; // set to error after setup
      const logLevels = { info: 1, warn: 2, error: 3 };

      function assert(condition, message, { messageType = 'error', exit = false, docsLink }) {
        if (!condition) {
          const formattedMessage =
            (subs.key ? `${subs.key}` : '') + ': ' + message + (docsLink ? `\n\nCheck out ${docsLink}` : '');
          if (exit) throw Error(formattedMessage);
          if (!console[messageType]) messageType = 'error';
          if (logLevels[messageType] >= logLevels[subs.logLevel]) console[messageType](formattedMessage);
          return true;
        }
        return false;
      }

      assert($planPicker, 'No $planPicker provided', { exit: true });
      subs.$planPicker = $planPicker;

      // KEY
      subs.key = $planPicker.getAttribute('subs-plan-picker'); //product Id
      const keyCount = document.querySelectorAll(`[subs-plan-picker="${subs.key}"]`).length;
      assert(keyCount === 1, `Key needs to be unique, key: '${subs.key}' used ${keyCount} times`, { exit: true });

      // DISCOUNT FORMAT
      subs.discountFormat = $planPicker.getAttribute('subs-discount-format');
      assert(subs.discountFormat === 'percent' || subs.discountFormat === 'absolute', 'Invalid discount format', {
        exit: true,
      });

      // SELECTORS
      subs.selectors = {
        sellingPlanId: 'input[name="selling_plan"]',
        variantId: '[name="id"]',
        productJson: '[subs-product-json]',
        onetime: '[subs-one-time]',
        groupContainer: '[subs-group-container]',
        subscriptionGroupContainer: '[subs-group-container-subscription]',
        sellingPlanGroup: (id) => `[subs-selling-plan-group${id ? `="${id}"` : ''}]`,
        groupInput: `input[name="subs-group-${subs.key}"]`,
        sellingPlans: (id) => `[subs-selling-plans${id ? `="${id}"` : ''}]`,
        onetimePrice: '[subs-onetime-price]',
        noGroup: '[no-group-container]',
        subscriptionPrice: '[subs-subscription-price]',
        subscriptionPriceCross: '[subs-subscription-price-cross]',
        discount: '[subs-discount]',
        discountProperty: '[name="properties[Discount]"]',
        externalOnetimePrice: '[subs-external-onetime-price]',
        externalSubscriptionPrice: '[subs-external-subscription-price]',
      };

      // FORM
      let $$sellingPlan = document.querySelector('.product-form').querySelectorAll(subs.selectors.sellingPlanId);
      // console.log($$sellingPlan);
      if ($planPicker.querySelectorAll(subs.selectors.sellingPlanId).length > 1) {
        $$sellingPlan = $planPicker.querySelectorAll(subs.selectors.sellingPlanId);
      }
      // console.log($$sellingPlan);

      assert($$sellingPlan.length !== 0, 'No selling plan input element found', { exit: true });
      assert($$sellingPlan.length === 1, 'More than 1 selling plan input element found', { messageType: 'warn' });
      const $sellingPlan = $$sellingPlan[0];
      subs.formAttr = $sellingPlan.getAttribute('form');

      if (subs.formAttr) subs.form = document.getElementById(subs.formAttr);
      if (!subs.form) subs.form = $planPicker.closest('product-info').querySelector('form[action*="/cart/add"]');

      assert(subs.form, "Couldn't find form, either connect to a form or submit using javascript", {
        messageType: 'warn',
      });

      // PRODUCT
      if (overrides.product) subs.product = overrides.product;
      else {
        const $$productJson = $planPicker.querySelectorAll(subs.selectors.productJson);
        assert($$productJson.length > 0, 'No product json element found', {
          exit: true,
        });
        assert($$productJson.length === 1, 'Multiple product json elements found', { messageType: 'warn' });
        subs.product = JSON.parse($$productJson[0].innerHTML);
      }
      assert(subs.product, 'Product is required', { exit: true });

      subs.isSubscription = false;
      // console.log('subs', subs);

      // MAIN PRODUCT
      const urlPath = window.location.pathname.split('/');
      subs.mainProduct =
        $planPicker.hasAttribute('subs-main-product') && urlPath[urlPath.length - 1] === subs.product.handle;

      // VARIANT ID
      const validateVariantId = (variantId) => {
        // console.log('validateVariantId');
        try {
          const type = typeof variantId;
          const isNumber = type === 'number';
          assert(isNumber, `Variant id needs to be a number, got '${type}'`, {
            exit: true,
          });
          const index = subs.product.variants.findIndex((variant) => variant.id == variantId);
          const found = index !== -1;
          assert(found, `Variant with id '${variantId}' not found`, {
            exit: true,
          });
          return true;
        } catch (err) {
          console.error(err);
          return false;
        }
      };

      const getVariantId = () => {
        // console.log('getVariantId()');
        try {
          // Override
          if (typeof overrides.getVariantId === 'function') {
            const variantId = overrides.getVariantId();
            assert(validateVariantId(variantId), `Invalid variant id '${variantId}'`, { exit: true });
            subs.variantId = variantId;
            return variantId;
          }
          // Default
          if (subs.form) {
            const $variantId = subs.form.querySelector(subs.selectors.variantId);
            assert($variantId, `Can't find variant id element using selector '${subs.selectors.variantId}'`, {
              exit: true,
            });
            const variantId = parseInt($variantId.value);
            assert(validateVariantId(variantId), `Invalid variant id '${variantId}'`, {
              messageType: 'warn',
              exit: true,
            });
            subs.variantId = variantId;
            return variantId;
          }
          assert(false, 'Failed to get variant id. No form and no getVariantId() override provided', { exit: true });
        } catch (err) {
          console.error(err);
          subs.variantId = null;
          return null;
        }
      };

      const getVariant = () => {
        // console.log('getVariant()');
        const variantId = getVariantId();
        if (variantId === null) return null;
        const variant = subs.product.variants.find((variant) => variant.id === variantId);
        subs.variant = variant;
        return variant;
      };

      // SELLING PLAN GROUPS
      const getAvailableSellingPlanGroupIds = () => {
        // console.log('getAvailableSellingPlanGroupIds');
        const variant = getVariant();
        const availableSellingPlanGroupIdsSet = new Set();
        variant.selling_plan_allocations.forEach((selling_plan_allocation) => {
          const selling_plan_group_id = selling_plan_allocation.selling_plan_group_id;
          const selling_plan_group = subs.product.selling_plan_groups.find(
            (selling_plan_group) => selling_plan_group.id === selling_plan_group_id,
          );
          // if (selling_plan_group.name === 'Subscription' || selling_plan_group.name === 'Prepaid')
          availableSellingPlanGroupIdsSet.add(selling_plan_allocation.selling_plan_group_id);
        });
        const availableSellingPlanGroupIds = Array.from(availableSellingPlanGroupIdsSet);
        subs.availableSellingPlanGroupIds = availableSellingPlanGroupIds;
        return availableSellingPlanGroupIds;
      };

      /* The above JavaScript code defines a function `subs.updateSellingPlanGroupAvailability` that is
      responsible for updating the availability of selling plan groups based on the available selling plan
      group IDs. Here is a summary of what the code does: */
      subs.updateSellingPlanGroupAvailability = () => {
        // console.log('updateSellingPlanGroupAvailability');
        try {
          const availableSellingPlanGroupIds = getAvailableSellingPlanGroupIds();
          assert(availableSellingPlanGroupIds !== null, 'Issue getting available selling plan group ids', {
            exit: true,
          });
          const $$sellingPlanGroup = $planPicker.querySelectorAll(subs.selectors.sellingPlanGroup());
          // console.log('seelingplanGroup', $$sellingPlanGroup);
          let checkedNoLongerAvailable = false;
          let checkedSellingPlanVariant = false;

          $$sellingPlanGroup.forEach(($sellingPlanGroup) => {
            const sellingPlanGroupId = $sellingPlanGroup.getAttribute('subs-selling-plan-group');
            // console.log(availableSellingPlanGroupIds);
            const isAvailable = availableSellingPlanGroupIds.includes(sellingPlanGroupId);
            // console.log('sellingPlanGroupId', sellingPlanGroupId);
            $sellingPlanGroup.disabled = !isAvailable;
            const $groupContainer = $sellingPlanGroup.closest(subs.selectors.groupContainer);

            if (isAvailable) {
              $groupContainer.classList.add('subs-group-container--available');
              checkedSellingPlanVariant = true;
            } else {
              $groupContainer.classList.remove('subs-group-container--available');
            }

            if ($sellingPlanGroup.checked && !isAvailable) {
              checkedNoLongerAvailable = true;
              $sellingPlanGroup.checked = false;
            }

            const $sellingPlans = $planPicker.querySelector(subs.selectors.sellingPlans(sellingPlanGroupId));

            if ($sellingPlans) {
              let options = $sellingPlans.querySelectorAll('option');
              let hasOptionBeenSelected = false;
              let selectedOption = $sellingPlans.value;
              let availableOptions = subs.variant.selling_plan_allocations.map((alloc) =>
                alloc.selling_plan_id.toString(),
              );
              let isSelectedOptionAvailable = availableOptions.includes(selectedOption);

              options.forEach((option) => {
                let isOptionAvailable = availableOptions.includes(option.value);

                option.removeAttribute('hidden');
                option.removeAttribute('disabled');
                option.removeAttribute('selected');

                if (!isOptionAvailable) {
                  option.setAttribute('hidden', true);
                  option.setAttribute('disabled', true);
                }

                if (isSelectedOptionAvailable) {
                  if (option.value === selectedOption && isOptionAvailable) {
                    hasOptionBeenSelected = true;
                    option.setAttribute('selected', true);
                  }
                } else if (isOptionAvailable && !hasOptionBeenSelected) {
                  hasOptionBeenSelected = true;
                  option.setAttribute('selected', true);
                  $sellingPlans.value = option.value;
                }
              });

              //update selling plan input value
              let $$sellingPlan = document
                .querySelector('.product-form')
                ?.querySelectorAll(subs.selectors.sellingPlanId);
              if ($planPicker.querySelectorAll(subs.selectors.sellingPlanId).length > 1) {
                $$sellingPlan = $planPicker.querySelectorAll(subs.selectors.sellingPlanId);
              }
              assert($sellingPlan, "Couldn't find selling plan element", {
                exit: true,
              });
              $sellingPlan.value = $sellingPlans.value;
            }
          });
          if (checkedNoLongerAvailable) {
            const $onetime = $planPicker.querySelector(subs.selectors.onetime);
            // console.log($onetime);
            if ($onetime && availableSellingPlanGroupIds.length === 0) $onetime.checked = true;
            else {
              const $firstAvailableSellingPlanGroup = $planPicker.querySelector(
                `${subs.selectors.sellingPlanGroup()}:not(:disabled)`,
              );
              if ($firstAvailableSellingPlanGroup) $firstAvailableSellingPlanGroup.checked = true;
            }
          }

          subs.updateSellingPlan();

          // --- Gift Variant Hidden Input Logic ---
          const container = document.getElementById('product-extra-prices');
          if (container) {
            container.querySelectorAll('input[type="hidden"][name="items[1]id"]').forEach((el) => el.remove());
            const checkedGiftRadio = $planPicker.querySelector('.subs-group-input[data-gift]:checked');
            if (checkedGiftRadio) {
              const dataGiftValue = checkedGiftRadio.getAttribute('data-gift');
              if (dataGiftValue) {
                const hiddenInput = document.createElement('input');
                hiddenInput.type = 'hidden';
                hiddenInput.name = 'items[1]id';
                hiddenInput.value = dataGiftValue;
                container.appendChild(hiddenInput);
              }
            }
          }
          // --- End Gift Variant Hidden Input Logic ---
        } catch (err) {
          console.error(err);
        }
      };

      /**
       * The function `getSelectedSellingPlanGroupId` selects a selling plan group based on user input and
       * handles error cases.
       * @returns The `getSelectedSellingPlanGroupId` function returns the `selectedSellingPlanGroupId`
       * value, which is either the ID of the selected selling plan group or `null` in case of errors or when
       * the one-time option is selected.
       */
      const getSelectedSellingPlanGroupId = () => {
        // console.log('getSelectedSellingPlanGroupId()');
        try {
          $planPicker
            .querySelectorAll(subs.selectors.groupInput)
            .forEach((el) =>
              el.closest(subs.selectors.groupContainer).classList.remove('subs-group-container--selected'),
            );
          const $onetime = $planPicker.querySelector(subs.selectors.onetime);
          if ($onetime?.checked) {
            $onetime.closest(subs.selectors.groupContainer).classList.add('subs-group-container--selected');
            subs.selectedSellingPlanGroupId = null;
            return null;
          }
          const $selectedSellingPlanGroup = $planPicker.querySelector(`${subs.selectors.sellingPlanGroup()}:checked`);
          if (assert($selectedSellingPlanGroup, 'No selected selling plan group', { messageType: 'warn' })) return null;
          $selectedSellingPlanGroup
            .closest(subs.selectors.groupContainer)
            .classList.add('subs-group-container--selected');
          const selectedSellingPlanGroupId = $selectedSellingPlanGroup.getAttribute('subs-selling-plan-group');
          assert(selectedSellingPlanGroupId, 'No selected selling plan group id', { exit: true });
          subs.selectedSellingPlanGroupId = selectedSellingPlanGroupId;
          return selectedSellingPlanGroupId;
        } catch (err) {
          console.error(err);
          subs.selectedSellingPlanGroupId = null;
          return null;
        }
      };

      // SELLING PLAN
      const validateSellingPlanId = (sellingPlanId) => {
        // console.log('validateSellingPlanId');
        try {
          const type = typeof sellingPlanId;
          const isNumber = type === 'number';
          assert(isNumber, `Selling plan id needs to be a number, got '${type}'`, { exit: true });
          const variant = getVariant();
          const index = variant.selling_plan_allocations.findIndex(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === sellingPlanId,
          );
          const found = index !== -1;
          assert(found, `Selling plan with id ${sellingPlanId} not found`, {
            exit: true,
          });
          return true;
        } catch (err) {
          console.error(err);
          return false;
        }
      };

      const changeStickyAddCart = () => {
        const stickyAddCarts = document.querySelectorAll('.jm-sticky-product .sticky-add-to-cart--price');
        if (stickyAddCarts.length > 0) {
          let planPickerElm = document.querySelector('.subs-plan-picker');
          let selectedPlan = planPickerElm.querySelector('.subs-group-container--selected .for_sticky_js');
          stickyAddCarts.forEach((stickyAddCart) => {
            //stickyAddCart.querySelector('.sticky-add-to-cart--price');
            if (selectedPlan) {
              stickyAddCart.innerHTML = selectedPlan.innerHTML;
            }
          });
        }
      };
      const updateUrlParam = (sellingPlanId) => {
        // console.log('update url param');
        if (sellingPlanId !== '' && !validateSellingPlanId(sellingPlanId)) sellingPlanId = '';
        // console.log('sellingPlanId', sellingPlanId);
        const url = new URL(window.location.href);
        if (sellingPlanId === '') url.searchParams.delete('selling_plan');
        else url.searchParams.set('selling_plan', sellingPlanId);
        changeStickyAddCart();
        window.history.pushState({}, '', url.href);
        // window.history.go(-1);
      };

      subs.updateSellingPlan = () => {
        // console.log('updateSellingPlan');
        try {
          let $$sellingPlan = document.querySelector('.product-form').querySelectorAll(subs.selectors.sellingPlanId);
          if ($planPicker.querySelectorAll(subs.selectors.sellingPlanId).length > 1) {
            $$sellingPlan = $planPicker.querySelectorAll(subs.selectors.sellingPlanId);
          }
          assert($sellingPlan, "Couldn't find selling plan element", {
            exit: true,
          });

          const sellingPlanGroupId = getSelectedSellingPlanGroupId();

          let sellingPlanId;
          let sellingPlanName;
          if (!sellingPlanGroupId) ((sellingPlanId = ''), (sellingPlanName = ''));
          else {
            const $sellingPlans = $planPicker.querySelector(subs.selectors.sellingPlans(sellingPlanGroupId));
            assert($sellingPlans, "Couldn't find selling plans element", {
              exit: true,
            });
            sellingPlanId = parseInt($sellingPlans.value);
            sellingPlanName = $sellingPlans.options[$sellingPlans.selectedIndex].text;
            assert(validateSellingPlanId(sellingPlanId), 'Invalid selling plan id', { exit: true });

            if ($planPicker.querySelectorAll(subs.selectors.sellingPlans()).length > 1) {
              $planPicker.querySelectorAll(subs.selectors.sellingPlans()).forEach((selector) => {
                let option = Array.from(selector.options).find(
                  (option) => option.text == sellingPlanName && option.getAttribute('hidden') == null,
                );
                if (option) selector.value = option.value;
              });
            }
          }

          subs.sellingPlanId = sellingPlanId;
          $sellingPlan.value = sellingPlanId;
          if (subs.mainProduct) updateUrlParam(sellingPlanId);
          updateDiscounts();
          updatePrices();
          $planPicker.dispatchEvent(new CustomEvent('subs:plan-picker:update', { detail: { subs } }));
        } catch (err) {
          console.error(err);
        }
      };

      // MONEY
      subs.moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window?.Shopify?.currency?.active || 'USD',
      });

      /**
       * Needs to run after updateSellingPlan()
       * The logic here should match the discount logic in subs-plan-picker.liquid
       */
      const updateDiscounts = () => {
        // console.log('updateDiscounts');
        subs.availableSellingPlanGroupIds.forEach((availableSellingPlanGroupId) => {
          const $sellingPlanGroup = $planPicker.querySelector(
            `${subs.selectors.sellingPlanGroup(availableSellingPlanGroupId)}`,
          );
          if (
            assert($sellingPlanGroup, `No selling group element found with id "${availableSellingPlanGroupId}"`, {
              messageType: 'info',
            })
          )
            return;
          const $sellingPlans = $sellingPlanGroup
            .closest(subs.selectors.groupContainer)
            .querySelector(subs.selectors.sellingPlans());
          if (
            assert($sellingPlans, 'No selling plans element found', {
              messageType: 'info',
            })
          )
            return;
          const $$discount = $sellingPlanGroup?.parentElement.querySelectorAll(subs.selectors.discount);
          if ($$discount.length === 0) return;
          const selling_plan_group = subs.product.selling_plan_groups.find(
            (selling_plan_group) => selling_plan_group.id === availableSellingPlanGroupId,
          );
          const selling_plan = selling_plan_group.selling_plans.find(
            (selling_plan) => selling_plan.id === parseInt($sellingPlans.value),
          );
          const price_adjustment = selling_plan.price_adjustments[0];

          let current_discount_percent = 0;
          let current_discount_absolute = 0;
          switch (price_adjustment.value_type) {
            case 'percentage':
              current_discount_percent = price_adjustment.value;
              current_discount_absolute = Math.round((subs.variant.price * price_adjustment.value) / 100.0);
              break;
            case 'fixed_amount':
              current_discount_percent = Math.round(((price_adjustment.value * 1.0) / subs.variant.price) * 100.0);
              current_discount_absolute = price_adjustment.value;
              break;
            case 'price':
              current_discount_percent = Math.round(
                (((subs.variant.price - price_adjustment.value) * 1.0) / subs.variant.price) * 100.0,
              );
              current_discount_absolute = subs.variant.price - price_adjustment.value;
              break;
          }
          let current_discount_text;
          if (current_discount_percent == 0) current_discount_text = '';
          else if (subs.discountFormat === 'percent') current_discount_text = `${current_discount_percent}%`;
          else current_discount_text = subs.moneyFormatter.format(current_discount_absolute / 100);

          $$discount.forEach((el) => (el.innerHTML = current_discount_text));
        });

        const $discountProperty = $planPicker.querySelector(subs.selectors.discountProperty);
        if ($discountProperty) {
          const $onetime = $planPicker.querySelector(subs.selectors.onetime);
          const $selectedSellingPlanGroup = $planPicker.querySelector(
            subs.selectors.sellingPlanGroup(subs.selectedSellingPlanGroupId),
          );
          const $$discount = $selectedSellingPlanGroup?.parentElement.querySelectorAll(subs.selectors.discount);
          if ($onetime?.checked || $$discount.length === 0) {
            $discountProperty.disabled = true;
            $discountProperty.value = '';
          } else {
            $discountProperty.disabled = false;
            const $discount = $selectedSellingPlanGroup?.parentElement.querySelector(subs.selectors.discount);
            if ($discount) $discountProperty.value = $discount.innerHTML;
          }
        }
      };

      const updatePrices = () => {
        // console.log('updatePrices');
        // Selling Plans
        subs.availableSellingPlanGroupIds.forEach((availableSellingPlanGroupId) => {
          const $sellingPlanGroup = $planPicker.querySelector(
            `${subs.selectors.sellingPlanGroup(availableSellingPlanGroupId)}`,
          );
          if (
            assert($sellingPlanGroup, `No selling group element found with id "${availableSellingPlanGroupId}"`, {
              messageType: 'info',
            })
          )
            return;
          const $sellingPlans = $sellingPlanGroup
            .closest(subs.selectors.groupContainer)
            .querySelector(subs.selectors.sellingPlans());
          if (
            assert($sellingPlans, 'No selling plans element found updatePrices', {
              messageType: 'info',
            })
          )
            return;
          // console.log('$sellingPlans', $sellingPlans);
          const sellingPlan = subs.product.selling_plan_groups
            .find((selling_plan_group) => selling_plan_group.id === availableSellingPlanGroupId)
            .selling_plans.find((selling_plan) => selling_plan.id === parseInt($sellingPlans.value));
          const $subscriptionPrice = $sellingPlanGroup?.parentElement.querySelector(subs.selectors.subscriptionPrice);
          if (
            assert($subscriptionPrice, 'No price element found', {
              messageType: 'info',
            })
          )
            return;
          const sellingPlanAllocation = subs.variant.selling_plan_allocations.find(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === sellingPlan.id,
          );
          //change subscription value
          // console.log('sellingPlanAllocation', subs.variant);
          const price = subs.moneyFormatter.format(sellingPlanAllocation.price / 100);

          // const priceWithoutFormat = sellingPlanAllocation.price / 100;

          $subscriptionPrice.innerHTML = price;
          $externalSubscriptionPrice = document.querySelector('[subs-external-subscription-price]');
          $sellingPlanGroup.checked ? (subs.isSubscription = true) : (subs.isSubscription = false);
          if ($externalSubscriptionPrice && subs.isSubscription) {
            $externalSubscriptionPrice.innerHTML = price;
          }
        });
        // One-time
        const $onetimePrice = $planPicker.querySelector(subs.selectors.onetimePrice);
        const $subscriptionPriceCrosses = document.querySelectorAll(subs.selectors.subscriptionPriceCross);
        const $externalOnetimePrice = document.querySelector(subs.selectors.externalOnetimePrice);
        const onetimePrice = subs.variant.price / 100;

        const onetimePriceFormatted = subs.moneyFormatter.format(onetimePrice);
        if ($onetimePrice) $onetimePrice.innerHTML = onetimePriceFormatted.replace('.00', '');
        if ($subscriptionPriceCrosses.length > 0) {
          $subscriptionPriceCrosses.forEach((subscriptionPriceCross) => {
            subscriptionPriceCross.innerHTML = onetimePriceFormatted.replace('.00', '');
          });
        }
        if ($externalOnetimePrice && !subs.isSubscription)
          $externalOnetimePrice.innerHTML = onetimePriceFormatted.replace('.00', '');
      };

      subs.update = () => subs.updateSellingPlanGroupAvailability();
      subs.update();

      // console.log('planpikker');
      // console.log('planpikker', subs.selectors.groupInput);
      // LISTENERS
      $planPicker
        .querySelectorAll(subs.selectors.groupInput)
        .forEach((el) => el.addEventListener('change', subs.updateSellingPlanGroupAvailability));

      $planPicker.querySelectorAll(subs.selectors.sellingPlans()).forEach((el) => {
        const handler = (e) => {
          const $sellingPlanGroup = e.currentTarget
            .closest(subs.selectors.groupContainer)
            .querySelector(subs.selectors.groupInput);
          if ($sellingPlanGroup.checked) subs.updateSellingPlan();
          else {
            $sellingPlanGroup.checked = true;
            subs.updateSellingPlan();
          }
        };
        el.addEventListener('change', handler);
        el.addEventListener('focus', handler);
      });

      subs.form.querySelector(subs.selectors.variantId).addEventListener('change', subs.update);

      // .querySelectorAll(subs.selectors.variantId)
      // .forEach(el => el.addEventListener('DOMSubtreeModified', subs.update));

      // .querySelector('INPUT CONTAINER SELECTOR')
      // .addEventListener('click', () => setTimeout(subs.update, 0));

      //no group event listiner
      // document.querySelector(subs.selectors.noGroup).addEventListener('click', () => {

      // });

      // Create MutationObserver instance to watch for change of selected option value inside select element for variant IDs
      subs.variantObserver = new MutationObserver(function (mutationsList, observer) {
        for (const mutation of mutationsList) {
          if (mutation.type === 'attributes') {
            subs.update();
          }
        }
      }).observe(subs.form.querySelector(subs.selectors.variantId), {
        attributeFilter: ['selected', 'value'],
        subtree: true,
      });

      // Fire init event
      $planPicker.dispatchEvent(new CustomEvent('subs:plan-picker:init', { detail: { subs } }));
      // Add to instances
      window.subsPlanPickerInstances.push(subs);
      // Attach to $planPicker
      $planPicker.subs = subs;

      return subs;
    } catch (err) {
      console.error(err);
    }
  };

  // Auto init
  window.subsPlanPickerAutoInit = () => {
    const $$autoInitPlanPicker = document.querySelectorAll('[subs-plan-picker][subs-auto-init]');
    $$autoInitPlanPicker.forEach(($autoInitPlanPicker) => {
      window.subsPlanPicker({ $planPicker: $autoInitPlanPicker });
    });
  };

  window.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.subs-plan-picker')) {
      let container = document.querySelector('.subs-plan-picker');
      let parentContainer = container.closest('.jm-product');
      if (parentContainer.querySelector('[name="selling_plan"]')) {
        window.subsPlanPickerAutoInit();
      } else {
        var input = document.createElement('input');
        container = parentContainer.querySelector('[data-type="add-to-cart-form"]');
        input.type = 'hidden';
        input.name = 'selling_plan';
        container.appendChild(input);
        window.subsPlanPickerAutoInit();
        // var interval = setInterval(updateSubscription, 100);
      }

      // function updateSubscription() {
      //   if (document.querySelectorAll('[name="selling_plan"]')) {
      //     console.log('first');
      //     window.subsPlanPickerAutoInit();
      //     clearInterval(interval);
      //   }
      // }
    }
  });

  window.dispatchEvent(new CustomEvent('subs:plan-picker:loaded'));
})();
